import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getSelection, $isRangeSelection, $insertNodes, createCommand, COMMAND_PRIORITY_EDITOR } from 'lexical';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from '@lexical/list';
import { $createCodeNode } from '@lexical/code';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { $createHorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { $createCalloutNode } from '../../LexicalEditor/nodes/CalloutNode';
import { $createChecklistItemNode } from '../../LexicalEditor/nodes/ChecklistItemNode';
import { $createEmbedNode } from '../../LexicalEditor/nodes/EmbedNode';
import { $createToggleNode } from '../../LexicalEditor/nodes/ToggleNode';
import { $createSubpageNode } from '../../LexicalEditor/nodes/SubpageNode';
import { $createTableOfContentsNode } from '../../LexicalEditor/nodes/TableOfContentsNode';
import { INSERT_COLUMN_LAYOUT_COMMAND } from './ColumnLayoutPlugin';
import { INSERT_IMAGE_COMMAND } from './ImagesPlugin';
import { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { $wrapNodes } from '@lexical/selection';
import { SLASH_COMMANDS } from './SlashCommandMenu';
import EmbedUrlModal from './EmbedUrlModal';
import useHandlePopoverClick from '../../../hooks/useHandlePopoverClick';
import { useDispatch } from 'react-redux';
import { togglePopover, updatePopoverProps } from '../../../redux/Slices/popoverSlice';
import { createPage } from '../../../api/pageApi';

export const INSERT_EMBED_COMMAND = createCommand('INSERT_EMBED_COMMAND');

export default function SlashCommandPlugin() {
  const [editor] = useLexicalComposerContext();
  const { handlePopoverClick } = useHandlePopoverClick();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [queryString, setQueryString] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [embedModalType, setEmbedModalType] = useState(null); // 'video' | 'audio' | 'file' | null
  const anchorRef = useRef(null);
  
  // Generate a unique ID for this editor instance's slash command trigger
  // We use a ref to keep it stable across renders
  const uniqueIdRef = useRef(`slash-command-trigger-${Math.random().toString(36).substr(2, 9)}`);
  const triggerId = uniqueIdRef.current;

  // Filter options based on query
  const filteredOptions = queryString
    ? SLASH_COMMANDS.filter((option) => {
        const query = queryString.toLowerCase();
        return (
          option.label.toLowerCase().includes(query) ||
          option.keywords.some((keyword) => keyword.toLowerCase().includes(query))
        );
      })
    : SLASH_COMMANDS;

  // Execute command
  const executeCommand = useCallback(
    (option) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        // Get the current node and find the slash
        const anchor = selection.anchor;
        const textNode = anchor.getNode();

        if (!textNode.isSimpleText()) return;

        const textContent = textNode.getTextContent();
        const slashIndex = textContent.lastIndexOf('/');

        if (slashIndex === -1) return;

        // Remove the slash and query text
        const beforeSlash = textContent.substring(0, slashIndex);
        const afterQuery = textContent.substring(anchor.offset);

        // Update text content
        textNode.setTextContent(beforeSlash + afterQuery);

        // Move cursor to where slash was
        const newOffset = slashIndex;
        selection.anchor.set(textNode.getKey(), newOffset, 'text');
        selection.focus.set(textNode.getKey(), newOffset, 'text');

        // Execute the command based on option key
        switch (option.key) {
          case 'paragraph':
            $wrapNodes(selection, () => $createParagraphNode());
            break;
          case 'h1':
            $wrapNodes(selection, () => $createHeadingNode('h1'));
            break;
          case 'h2':
            $wrapNodes(selection, () => $createHeadingNode('h2'));
            break;
          case 'h3':
            $wrapNodes(selection, () => $createHeadingNode('h3'));
            break;
          case 'bullet':
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            break;
          case 'number':
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            break;
          case 'quote':
            $wrapNodes(selection, () => $createQuoteNode());
            break;
          case 'code':
            $wrapNodes(selection, () => $createCodeNode());
            break;
          case 'table':
            editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns: '3', rows: '3' });
            break;
          case 'divider':
            selection.insertNodes([$createHorizontalRuleNode()]);
            break;
          case 'callout-info': {
            const calloutNode = $createCalloutNode('info');
            calloutNode.append($createParagraphNode());
            $insertNodes([calloutNode]);
            break;
          }
          case 'callout-warning': {
            const calloutNode = $createCalloutNode('warning');
            calloutNode.append($createParagraphNode());
            $insertNodes([calloutNode]);
            break;
          }
          case 'callout-success': {
            const calloutNode = $createCalloutNode('success');
            calloutNode.append($createParagraphNode());
            $insertNodes([calloutNode]);
            break;
          }
          case 'callout-error': {
            const calloutNode = $createCalloutNode('error');
            calloutNode.append($createParagraphNode());
            $insertNodes([calloutNode]);
            break;
          }
          case 'checklist': {
            const checklistNode = $createChecklistItemNode(false);
            checklistNode.append($createParagraphNode());
            $insertNodes([checklistNode]);
            break;
          }
          case 'toggle': {
            const toggleNode = $createToggleNode(true);
            toggleNode.append($createParagraphNode());
            $insertNodes([toggleNode]);
            break;
          }
          case 'video':
          case 'audio':
          case 'file': {
            setEmbedModalType(option.key);
            break;
          }
          case 'page':
            // Handled outside editor.update() below
            break;
          case 'columns-2':
          case 'columns-3':
          case 'columns-4': {
            const columnCount = parseInt(option.key.split('-')[1], 10);
            editor.dispatchCommand(INSERT_COLUMN_LAYOUT_COMMAND, { columnCount });
            break;
          }
          case 'toc': {
            const tocNode = $createTableOfContentsNode();
            $insertNodes([tocNode]);
            break;
          }
          default:
            break;
        }
      });

      // Handle page creation outside of editor.update() (async API call)
      if (option.key === 'page') {
        (async () => {
          try {
            const result = await createPage({ title: '', icon: '📄' });
            const page = result?.page;
            if (page?.shortId) {
              editor.update(() => {
                const subpageNode = $createSubpageNode(page.shortId, page.title || 'Untitled', page.icon || '📄');
                $insertNodes([subpageNode]);
              });
              navigate(`/p/${page.shortId}`);
            }
          } catch (error) {
            console.error('Failed to create page:', error);
          }
        })();
      }

      // Handle image upload outside of editor.update()
      if (option.key === 'image') {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = (e) => {
          const file = e.target.files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === 'string') {
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  altText: file.name,
                  src: reader.result,
                });
              }
            };
            reader.readAsDataURL(file);
          }
        };
        fileInput.click();
      }

      // Close popover
      dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
      setIsMenuOpen(false);
      setQueryString('');
      setSelectedIndex(0);
    },
    [editor, dispatch]
  );

  // Register INSERT_EMBED_COMMAND handler
  useEffect(() => {
    return editor.registerCommand(
      INSERT_EMBED_COMMAND,
      (payload) => {
        const { url, type } = payload;
        const embedNode = $createEmbedNode(url, type);
        $insertNodes([embedNode]);
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  // Handle embed modal submit
  const handleEmbedSubmit = useCallback((url, type) => {
    setEmbedModalType(null);
    editor.focus();
    // Dispatch after focus restores the selection
    setTimeout(() => {
      editor.dispatchCommand(INSERT_EMBED_COMMAND, { url, type });
    }, 0);
  }, [editor]);

  // Handle mouse enter
  const handleMouseEnter = useCallback((index) => {
    setSelectedIndex(index);
    
    // Update the popover props with new selectedIndex
    dispatch(updatePopoverProps({
      contentId: 'slashCommand',
      targetId: triggerId,
      props: {
        options: filteredOptions,
        selectedIndex: index,
        onSelect: executeCommand,
        onMouseEnter: handleMouseEnter,
        isFiltering: !!queryString,
        queryString: queryString,
        isClosed: false,
        pinned: true,
      }
    }));
  }, [filteredOptions, executeCommand, dispatch, triggerId, queryString]);

  // Helper to update anchor position
  const updateAnchorPosition = useCallback(() => {
    const domSelection = window.getSelection();
    if (domSelection && domSelection.rangeCount > 0) {
      const range = domSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      if (anchorRef.current) {
        // Calculate position relative to the offset parent (the editor container)
        const parent = anchorRef.current.offsetParent || document.body;
        const parentRect = parent.getBoundingClientRect();
        
        // Adjust for parent's position to place anchor exactly at cursor
        // We use Math.round to align with pixels
        const top = rect.bottom - parentRect.top;
        const left = rect.left - parentRect.left;
        
        anchorRef.current.style.top = `${top}px`;
        anchorRef.current.style.left = `${left}px`;
        anchorRef.current.style.height = `${rect.height}px`;
        
        // Use a more targeted event or rely on the ref update
        // We trigger a custom event on the anchor that usePopoverPositionUpdater could listen to?
        // Or just let the resize/scroll listeners handle it.
        // But popover needs to know anchor moved.
        // We can dispatch a global resize event to force update, as before, which is effective.
        window.dispatchEvent(new Event('resize'));
      }
    }
  }, []);

  // Listen for text changes to detect slash command
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          if (isMenuOpen) {
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
            setIsMenuOpen(false);
            setQueryString('');
          }
          return;
        }

        const anchor = selection.anchor;
        const textNode = anchor.getNode();
        const textContent = textNode.getTextContent();
        const cursorOffset = anchor.offset;

        // Find the last slash before cursor
        const textBeforeCursor = textContent.substring(0, cursorOffset);
        const slashIndex = textBeforeCursor.lastIndexOf('/');

        if (slashIndex !== -1) {
          // Check if there's a space before the slash (or it's at the start)
          const charBeforeSlash = slashIndex > 0 ? textContent[slashIndex - 1] : ' ';
          const isValidTrigger = charBeforeSlash === ' ' || slashIndex === 0;

          if (isValidTrigger) {
            const query = textBeforeCursor.substring(slashIndex + 1);

            // Only show menu if query doesn't contain spaces
            if (!query.includes(' ')) {
              setQueryString(query);

              // Update the anchor position and open menu
              if (!isMenuOpen) {
                // Initial positioning
                updateAnchorPosition();

                // Open popover
                handlePopoverClick(
                  { currentTarget: anchorRef.current }, // Pass the real element as target
                  'slashCommand',
                  {
                    options: filteredOptions,
                    selectedIndex: 0,
                    onSelect: executeCommand,
                    onMouseEnter: handleMouseEnter,
                    isFiltering: !!query,
                    queryString: query,
                    isClosed: false,
                    pinned: true,
                  }
                );

                setIsMenuOpen(true);
                setSelectedIndex(0);
              } else {
                // Menu is already open, update only the props without changing position
                // Reset selectedIndex if it's out of bounds
                const newSelectedIndex = selectedIndex >= filteredOptions.length
                  ? Math.max(0, filteredOptions.length - 1)
                  : selectedIndex;

                setSelectedIndex(newSelectedIndex);

                // Update the popover props without changing position
                dispatch(updatePopoverProps({
                  contentId: 'slashCommand',
                  targetId: triggerId,
                  props: {
                    options: filteredOptions,
                    selectedIndex: newSelectedIndex,
                    onSelect: executeCommand,
                    onMouseEnter: handleMouseEnter,
                    isFiltering: !!query,
                    queryString: query,
                    isClosed: false,
                    pinned: true,
                  }
                }));

                // Update anchor position if needed (e.g. if text moves while typing)
                updateAnchorPosition();
              }
              return;
            }
          }
        }

        // Close menu only when user explicitly moves away from slash command context
        // (e.g., deletes the '/', adds a space after the query, or moves cursor away)
        // The pinned prop prevents closing from external events like AI responses
        if (isMenuOpen) {
          dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
          setIsMenuOpen(false);
          setQueryString('');
        }
      });
    });
  }, [editor, isMenuOpen, filteredOptions, executeCommand, handleMouseEnter, handlePopoverClick, dispatch, triggerId, updateAnchorPosition]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const newIndex = (selectedIndex + 1) % filteredOptions.length;
        setSelectedIndex(newIndex);
        
        // Update the popover props with new selectedIndex
        dispatch(updatePopoverProps({
          contentId: 'slashCommand',
          targetId: triggerId,
          props: {
            options: filteredOptions,
            selectedIndex: newIndex,
            onSelect: executeCommand,
            onMouseEnter: handleMouseEnter,
            isFiltering: !!queryString,
            queryString: queryString,
            isClosed: false,
            pinned: true,
          }
        }));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const newIndex = (selectedIndex - 1 + filteredOptions.length) % filteredOptions.length;
        setSelectedIndex(newIndex);
        
        // Update the popover props with new selectedIndex
        dispatch(updatePopoverProps({
          contentId: 'slashCommand',
          targetId: triggerId,
          props: {
            options: filteredOptions,
            selectedIndex: newIndex,
            onSelect: executeCommand,
            onMouseEnter: handleMouseEnter,
            isFiltering: !!queryString,
            queryString: queryString,
            isClosed: false,
            pinned: true,
          }
        }));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (filteredOptions[selectedIndex]) {
          executeCommand(filteredOptions[selectedIndex]);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
        setIsMenuOpen(false);
        setQueryString('');
      }
    };

    // Use capture phase to ensure we catch the event before Lexical
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isMenuOpen, filteredOptions, selectedIndex, executeCommand, dispatch, handleMouseEnter, triggerId]);

  // Render inline to ensure it's part of the editor container flow
  return (
    <>
      <div
        id={triggerId}
        data-popover-trigger="true"
        ref={anchorRef}
        style={{
          position: 'absolute',
          width: 1,
          pointerEvents: 'none',
          visibility: 'hidden',
        }}
      />
      {embedModalType && (
        <EmbedUrlModal
          embedType={embedModalType}
          onSubmit={handleEmbedSubmit}
          onClose={() => setEmbedModalType(null)}
        />
      )}
    </>
  );
}

